import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useReducer } from 'react';
import { getEmployeeSearch, useStaffServiceClient, } from '@skbkontur/staff-common';
import { useStaffServiceMetrics } from '../MetricsProvider';
import { arrayToRecord, getInitialPeriod } from './EmployeeList.helpers';
import { reducer } from './EmployeeList.reducer';
export const EmployeeListContext = createContext({});
export const EmployeeListContextProvider = ({ children, listType, employerId, isSingleSelection, pageSize, additionalColumn, initialState, filters, fields, onContactSupport, checkIfEmployeePreSelected = () => false, onSubmit, }) => {
    const [state, dispatch] = useReducer(reducer, {
        status: 'loading',
        employees: [],
        page: 1,
        totalCount: 0,
        selectAll: 'none',
        selectedEmployees: {},
        preloadedEmployees: {},
        selectAllStatus: 'completed',
        employmentPeriodQuery: filters.includes('employmentPeriod') ? getInitialPeriod(initialState) : null,
        searchQuery: initialState?.search || '',
        editEmployee: undefined,
        references: null,
        reloadListFlag: false,
    });
    const client = useStaffServiceClient();
    const trackAction = useStaffServiceMetrics();
    const trackListAction = (action, options) => {
        trackAction('employeeList', action, { ...options, listType });
    };
    const listItemRequest = listType === 'employee' ? 'getEmployees' : 'getClients';
    const getReferences = useCallback(async () => {
        try {
            const referenceKeys = [];
            (fields.bankAccount || fields.organization) && referenceKeys.push('banks');
            fields.citizenship && referenceKeys.push('countries');
            fields.passport && referenceKeys.push('driverLicenseCategories', 'identityDocuments');
            fields.residentStatus && referenceKeys.push('residentStatuses');
            const references = await client.getReferences(referenceKeys);
            dispatch({ type: 'setReferences', value: references });
        }
        catch (error) {
            dispatch({ type: 'setStatus', value: 'error' });
        }
    }, [client]);
    const preloadEmployees = useCallback(async (chunkSize = 1000, endStatus = 'success') => {
        try {
            dispatch({ type: 'setSelectAllStatus', value: 'loading' });
            const { totalCount } = await client[listItemRequest](employerId, {
                name: state.searchQuery || undefined,
                count: 1,
                activePeriod: state.employmentPeriodQuery || undefined,
            });
            const requests = [];
            for (let index = 0; index <= totalCount / chunkSize; index++) {
                const search = getEmployeeSearch(state.searchQuery);
                const query = {
                    [search.type]: search.value || undefined,
                    skip: chunkSize * index,
                    count: chunkSize,
                    sort: ['id'],
                    activePeriod: state.employmentPeriodQuery || undefined,
                };
                requests.push(client[listItemRequest](employerId, query));
            }
            const results = await Promise.all(requests);
            const employees = results
                .map((el) => el.items)
                .reduce((acc, cur) => {
                acc.push(...cur);
                return acc;
            }, [])
                .filter((el) => !checkIfEmployeePreSelected(el));
            dispatch({ type: 'setPreloadedEmployees', value: arrayToRecord(employees) });
            employees.length === 0 && dispatch({ type: 'setSelectAll', value: 'none' });
            dispatch({ type: 'setSelectAllStatus', value: employees.length === 0 ? 'completed' : endStatus });
            if (endStatus === 'completed') {
                const filtered = employees.filter((el) => state.selectedEmployees[el.id]);
                if (filtered.length === 0) {
                    dispatch({ type: 'setSelectAll', value: 'none' });
                    return;
                }
                if (filtered.length === employees.length) {
                    dispatch({ type: 'setSelectAll', value: 'all' });
                    return;
                }
                dispatch({ type: 'setSelectAll', value: 'indeterminate' });
            }
        }
        catch (error) {
            dispatch({ type: 'setPreloadedEmployees', value: {} });
            dispatch({ type: 'setSelectAllStatus', value: 'aborted' });
            dispatch({ type: 'setStatus', value: 'error' });
        }
    }, [client, employerId, listItemRequest, state.searchQuery, state.employmentPeriodQuery]);
    const getEmployees = useCallback(async (page) => {
        try {
            const search = getEmployeeSearch(state.searchQuery);
            const query = {
                [search.type]: search.value || undefined,
                skip: (page - 1) * pageSize,
                count: pageSize,
                activePeriod: state.employmentPeriodQuery || undefined,
            };
            const result = await client[listItemRequest](employerId, query);
            dispatch({ type: 'setEmployees', value: result });
            dispatch({ type: 'setStatus', value: result.totalCount === 0 ? 'empty' : 'success' });
        }
        catch (error) {
            dispatch({ type: 'setEmployees', value: { items: [], totalCount: 0 } });
            dispatch({ type: 'setStatus', value: 'error' });
        }
    }, [client, employerId, listItemRequest, pageSize, state.searchQuery, state.employmentPeriodQuery]);
    const handleSelectAll = async (newSelectedState) => {
        trackListAction('selectEmployee', {
            target: 'all',
            selected: newSelectedState,
        });
        if (newSelectedState) {
            dispatch({ type: 'setSelectAll', value: 'all' });
            Object.keys(state.preloadedEmployees).length > 0 &&
                dispatch({
                    type: 'setSelectedEmployees',
                    value: {
                        ...state.selectedEmployees,
                        ...state.preloadedEmployees,
                    },
                });
            return;
        }
        dispatch({ type: 'setSelectAll', value: 'none' });
        const withoutPreloaded = { ...state.selectedEmployees };
        for (const key of Object.keys(withoutPreloaded)) {
            if (state.preloadedEmployees[key]) {
                delete withoutPreloaded[key];
            }
        }
        dispatch({ type: 'setSelectedEmployees', value: withoutPreloaded });
    };
    const toggleSelectedEmployee = (employee) => {
        trackListAction('selectEmployee', {
            target: employee.id,
            selected: !!state.selectedEmployees[employee.id],
        });
        const clone = { ...state.selectedEmployees };
        const preloadedKeys = Object.keys(state.preloadedEmployees);
        if (clone[employee.id]) {
            delete clone[employee.id];
            if (state.selectAll === 'all') {
                dispatch({ type: 'setSelectAll', value: 'indeterminate' });
            }
            if (state.selectAllStatus === 'completed' && preloadedKeys.every((el) => !clone[el])) {
                dispatch({ type: 'setSelectAll', value: 'none' });
            }
        }
        else {
            clone[employee.id] = employee;
            if (state.selectAll === 'none') {
                dispatch({ type: 'setSelectAll', value: 'indeterminate' });
            }
            if (preloadedKeys.length > 0 && preloadedKeys.every((el) => clone[el])) {
                dispatch({ type: 'setSelectAll', value: 'all' });
            }
            if (preloadedKeys.length === 0 &&
                state.employees.length === state.totalCount &&
                state.employees.filter((el) => !checkIfEmployeePreSelected(el)).every((el) => clone[el.id])) {
                dispatch({ type: 'setSelectAll', value: 'all' });
            }
        }
        dispatch({ type: 'setSelectedEmployees', value: clone });
    };
    const handlePageChange = (value) => {
        trackListAction('navigateEmployeeList', {
            page: value,
            searchQuery: state.searchQuery,
        });
        dispatch({ type: 'setStatus', value: 'loading' });
        dispatch({ type: 'setPage', value });
    };
    const handleEmploymentPeriodChange = (value) => {
        if (state.employmentPeriodQuery?.begin === value.begin && state.employmentPeriodQuery?.end === value.end) {
            return;
        }
        trackListAction('filterEmployeeList', {
            filters: {
                searchQuery: state.searchQuery,
                employmentPeriodQuery: value,
            },
        });
        dispatch({ type: 'setStatus', value: 'loading' });
        dispatch({ type: 'setEmploymentPeriodQuery', value });
        dispatch({ type: 'setPage', value: 1 });
    };
    const handleSearchChange = (value) => {
        if (state.searchQuery === value) {
            return;
        }
        trackListAction('searchEmployeeList', { searchQuery: value });
        dispatch({ type: 'setStatus', value: 'loading' });
        dispatch({ type: 'setSearchQuery', value });
        dispatch({ type: 'setPage', value: 1 });
    };
    const handleListReload = () => {
        trackListAction('reloadEmployeeList');
        dispatch({ type: 'setReloadListFlag', value: true });
    };
    const handleEditEmployee = (employee) => {
        // null means create new employee
        dispatch({ type: 'setEditEmployee', value: employee });
    };
    const handleCloseEmployee = () => {
        // undefined means close employee card
        dispatch({ type: 'setEditEmployee', value: undefined });
    };
    const handleReloadCurrentPage = () => {
        dispatch({ type: 'setReloadListFlag', value: true });
        dispatch({ type: 'setPreloadedEmployees', value: {} });
        state.selectAll === 'all' && dispatch({ type: 'setSelectAll', value: 'indeterminate' });
    };
    const handleSubmit = async (employees) => {
        try {
            trackListAction('submitEmployeeList', { selectedCount: employees.length });
            await onSubmit(employees);
            trackListAction('successfullySubmitEmployeeList', { submittedCount: employees.length });
        }
        catch (error) {
            dispatch({ type: 'setStatus', value: 'criticalError' });
        }
    };
    const handleEmploymentPeriodTodayClick = (currentDate) => {
        trackListAction('filterEmployeeListTodayClick', { currentDate });
    };
    const handleEmploymentPeriodAllTimeClick = () => {
        trackListAction('filterEmployeeListAllTimeClick');
    };
    // загрузка справочников
    useEffect(() => {
        if (state.references) {
            return;
        }
        getReferences();
    }, [state.references, getReferences]);
    // получение списка сотрудников
    useEffect(() => {
        if (!employerId || !client) {
            dispatch({ type: 'setStatus', value: 'criticalError' });
            return;
        }
        getEmployees(state.page);
    }, [employerId, client, state.page, getEmployees]);
    // перезагрузка текущей страницы по флагу
    useEffect(() => {
        if (state.reloadListFlag) {
            dispatch({ type: 'setReloadListFlag', value: false });
            getEmployees(state.page);
        }
    }, [getEmployees, state.reloadListFlag, state.page]);
    // актуализация выбранных сотрудников в соответствии со списком (например когда выбранного редактировали)
    useEffect(() => {
        dispatch({ type: 'updateSelectedEmployees', value: state.employees });
    }, [state.employees]);
    // если пытаемся выбрать всех, но нет preloadedEmployees
    useEffect(() => {
        if (state.selectAll === 'all' && Object.keys(state.preloadedEmployees).length === 0) {
            const filtered = state.employees.filter((el) => !checkIfEmployeePreSelected(el));
            filtered.length > 0 &&
                dispatch({
                    type: 'updateSelectedEmployees',
                    value: filtered,
                });
            preloadEmployees(1000);
            return;
        }
    }, [state.selectAll, state.preloadedEmployees]);
    // проверки когда выбраны все
    useEffect(() => {
        if (state.selectAll === 'all' && state.selectAllStatus === 'success') {
            dispatch({
                type: 'setSelectedEmployees',
                value: { ...state.selectedEmployees, ...state.preloadedEmployees },
            });
            dispatch({ type: 'setSelectAllStatus', value: 'completed' });
            return;
        }
        if (state.selectAll === 'indeterminate' && state.selectAllStatus === 'success') {
            const preloaded = { ...state.preloadedEmployees };
            state.employees.forEach((employee) => {
                !state.selectedEmployees[employee.id] && delete preloaded[employee.id];
            });
            dispatch({ type: 'setSelectedEmployees', value: { ...state.selectedEmployees, ...preloaded } });
            dispatch({ type: 'setSelectAllStatus', value: 'completed' });
            return;
        }
    }, [state.selectAll, state.selectAllStatus]);
    // если в момент перезагрузки списка кто-то выбран, но грузим  preloadedEmployees
    useEffect(() => {
        if (Object.keys(state.selectedEmployees).length === 0 && Object.keys(state.preloadedEmployees).length === 0) {
            return;
        }
        preloadEmployees(1000, 'completed');
    }, [getEmployees]);
    return (_jsx(EmployeeListContext.Provider, { value: {
            employerId,
            state,
            listType,
            isSingleSelection,
            selectedCount: state.selectAllStatus === 'loading' ? 0 : Object.keys(state.selectedEmployees).length,
            totalPages: Math.ceil(state.totalCount / pageSize),
            additionalColumn,
            fields,
            toggleSelectedEmployee,
            handlePageChange,
            handleSelectAll,
            handleEmploymentPeriodChange,
            handleSearchChange,
            handleEditEmployee,
            handleCloseEmployee,
            handleReloadCurrentPage,
            handleListReload,
            handleContactSupport: onContactSupport,
            checkIfEmployeePreSelected,
            onSubmit: handleSubmit,
            handleEmploymentPeriodAllTimeClick,
            handleEmploymentPeriodTodayClick,
            trackListAction,
        }, children: children }));
};
